var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getDiscoverSubjects } from "@piiqtechnologies/ui-lib";
import { useOptionalActiveOrganization } from "@stores/ActiveOrganizationContext";
// The server caps limit at 3000 and the largest named customer has ~170 communities, so the
// list is fetched whole and filtered in the browser. Server-side paging and search are the
// lift past ~1,000 communities, not this ticket.
const COMMUNITY_FETCH_LIMIT = 3000;
const SidebarNavContext = createContext(null);
export const useSidebarNavContext = () => {
    const context = useContext(SidebarNavContext);
    if (!context) {
        throw new Error("useSidebarNavContext must be used inside SidebarNavProvider");
    }
    return context;
};
/**
 * The nav's own slice of data: the community list, and nothing else. Deliberately not a hoist
 * of AiDiscoverabilityProvider — that one carries the org-wide /api/reports fetch, which
 * AdminHome, Overview and Community must stay outside of (see Routes.tsx).
 *
 * includeLatestReport makes the list carry each community's most recent completed report, so
 * expanding one costs no request at all.
 */
export const SidebarNavProvider = ({ children }) => {
    var _a;
    // The `/o/:orgId` segment, not the token's organization: a member of several sees the sidebar
    // for the organization they are in, and switching organizations refetches. Staff render the
    // shell with no segment above them, so they keep the account-wide list the token allows.
    const organizationId = (_a = useOptionalActiveOrganization()) === null || _a === void 0 ? void 0 : _a.activeOrgId;
    const [communities, setCommunities] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeCommunityId, setActiveCommunityId] = useState(undefined);
    // The organization is sent now that there is one to send. The server re-authorizes it against
    // the caller's memberships, so a hand-edited segment cannot widen what comes back.
    useEffect(() => {
        let active = true;
        const load = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            setLoading(true);
            setError(null);
            try {
                const axiosResponse = yield getDiscoverSubjects(Object.assign({ limit: COMMUNITY_FETCH_LIMIT, sort: "name", includeLatestReport: true }, (organizationId && { organizationId })));
                const response = axiosResponse;
                if (!active)
                    return;
                setCommunities((_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.subjects) !== null && _b !== void 0 ? _b : []);
                setTotal((_d = (_c = response === null || response === void 0 ? void 0 : response.data) === null || _c === void 0 ? void 0 : _c.total) !== null && _d !== void 0 ? _d : 0);
            }
            catch (caught) {
                if (!active)
                    return;
                setCommunities([]);
                setTotal(0);
                setError(caught);
            }
            finally {
                if (active)
                    setLoading(false);
            }
        });
        load();
        return () => {
            active = false;
        };
    }, [organizationId]);
    const value = useMemo(() => ({ communities, total, loading, error, activeCommunityId, setActiveCommunityId }), [communities, total, loading, error, activeCommunityId]);
    return React.createElement(SidebarNavContext.Provider, { value: value }, children);
};
